import { useEffect, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

// Polls pending platform_fee_accruals every second so any reserved fees stay
// invisibly deducted from displayed balances across the wallet UI.
export function usePendingFees(pollMs: number = 1000) {
  const { user } = useAuth();
  const [reservedBySymbol, setReservedBySymbol] = useState<Record<string, number>>({});
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    if (!user) { setReservedBySymbol({}); return; }

    let cancelled = false;
    const fetchOnce = async () => {
      const { data, error } = await supabase
        .from("platform_fee_accruals")
        .select("fee_symbol, fee_amount")
        .eq("user_id", user.id)
        .eq("status", "pending");
      if (cancelled || !mounted.current) return;
      if (error || !data) return;
      const map: Record<string, number> = {};
      for (const row of data as Array<{ fee_symbol: string; fee_amount: number | string }>) {
        const sym = String(row.fee_symbol || "").toUpperCase();
        map[sym] = (map[sym] || 0) + Number(row.fee_amount || 0);
      }
      setReservedBySymbol(map);
    };

    fetchOnce();
    const iv = setInterval(fetchOnce, pollMs);
    return () => { cancelled = true; mounted.current = false; clearInterval(iv); };
  }, [user, pollMs]);

  const reservedOf = (symbol: string) => reservedBySymbol[String(symbol || "").toUpperCase()] || 0;
  return { reservedBySymbol, reservedOf };
}
